import { GT_User } from '../../types/globals'
import { Goehler_Tool_REST_Client } from '@app/utils/rest-client'
import { Api_Error, handle_api_error } from '@app/utils/api-error'
import { GT_My_Running_Time, GT_Stop_Time_Check, GT_Time } from '../../types/times'
import { duration_in_minutes, minutes_to_duration } from '@app/utils/date'
import { bus } from '@app/core/bus'
import { Toast } from '@app/utils/toast'
import { Duration } from 'luxon'
import ClickEvent = JQuery.ClickEvent;

export abstract class Times_Controller {
    rest_client: Goehler_Tool_REST_Client

    protected constructor() {
        this.rest_client = new Goehler_Tool_REST_Client()
    }
}

export class App_Times_Controller extends Times_Controller {
    running_time: GT_My_Running_Time | null

    constructor() {
        super()

        this.running_time = gt_my_running_times[0] ?? null
    }

    is_project_context() {
        return typeof project !== 'undefined'
    }

    ready() {
        jQuery( '.reason-threshold' ).text( gt_time_config.reason_threshold )
        this.check_my_running_times()
        this.init_actions()
        this.init_events()
    }

    init_actions() {
        jQuery( '[data-action="stop-my-time"]' ).on( 'click', () => void this.api_stop_my_time_confirmation() )
    }

    /**
     * Aktualisiert die Leiste, wenn das Tablet aus dem Standby kommt.
     */
    init_events() {
        document.addEventListener( 'visibilitychange', () => {
            if ( document.visibilityState === 'visible' ) {
                void this.api_load_my_running_times()
            }
        } )
    }

    /**
     * Lädt die eigene laufende Arbeitszeit und aktualisiert die Leiste.
     */
    async api_load_my_running_times() {
        const TASK = 'Laden der eigenen Arbeitszeit'

        try {
            const response = await this.rest_client.get(
                'goehler-tool/v1/times/my-running-times'
            ) as GT_My_Running_Time[]

            const next = response[0] ?? null
            const changed = next?.post_id !== this.running_time?.post_id

            this.running_time = next

            this.check_my_running_times()

            if ( changed ) {
                bus.emit( 'times:changed' )
            }
        } catch ( error ) {

            // Netzwerkfehler beim Hintergrund-Refresh (Standby, Seitenwechsel).
            // sind kein Zustand, über den der Mitarbeiter informiert werden muss.
            if ( error instanceof Api_Error && error.status === 0 ) {
                console.warn( 'Leiste konnte nicht aktualisiert werden', error )
                return
            }

            handle_api_error( error, TASK )
        }
    }

    /**
     * Prüft den aktuellen Stand und öffnet die Bestätigung.
     */
    async api_stop_my_time_confirmation() {
        const TASK = 'Prüfen der Arbeitszeit'

        this.disable_stop_button()

        try {
            const check = await this.rest_client.get(
                'goehler-tool/v1/times/stop-time-check'
            ) as GT_Stop_Time_Check

            if ( ! check.running ) {
                app.show_toast_ajax_response( 'Es läuft keine Arbeitszeit.' )

                await this.api_load_my_running_times()
                return
            }

            this.show_stop_modal( check )
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_stop_button()
        }
    }

    /**
     * Öffnet die Bestätigung zum Beenden der eigenen Arbeitszeit.
     *
     * @param check  Antwort der Vorabprüfung.
     */
    show_stop_modal( check: GT_Stop_Time_Check ) {
        const modal = app.get_modal( 'stop-time-confirmation' )

        if ( ! modal ) {
            return
        }

        const project_badge = modal.find( '.stop-time-context span.badge' )

        project_badge.attr( 'data-project-status', check.status )
        project_badge.text( check.project.title )

        // In der Montage werden alle Arbeitszeiten des Bereichs gemeinsam beendet.
        const plural = check.is_assembly

        modal.find( '.modal-title' ).text( plural ? 'Arbeitszeiten beenden' : 'Arbeitszeit beenden' )
        modal.find( '.button-title' ).text( plural ? 'Arbeitszeiten beenden' : 'Arbeitszeit beenden' )
        modal.find( '.stop-time-hint' ).text( plural ? 'Damit werden alle laufenden Arbeitszeiten in der Zeitübersicht beendet.' : 'Damit wird die laufende Arbeitszeit in der Zeitübersicht beendet.')

        const workers_container = modal.find( '.assembly-workers' )
        const workers_list = workers_container.find( '.user-list' )

        workers_container.prop( 'hidden', ! check.is_assembly )
        modal.find( '.stop-time-hint' ).prop( 'hidden', check.is_assembly )

        if ( check.is_assembly ) {
            workers_list.empty()

            check.workers.forEach( ( name ) => {
                workers_list.append( jQuery( '<span class="badge"></span>' ).text( name ) )
            } )
        }

        const form = modal.find( '#form-project-status-reason-edit' )
        const textarea = modal.find( '#project-status-reason-edit' )
        const approve_button = modal.find( '.positive.button' )

        /**
         * Prüft, ob die Begründungs-Textarea leer ist.
         */
        const textarea_is_empty = () => String( textarea.val() ?? '' ).trim() === ''

        /**
         * Blockiert den Approve-Button, solange die Textarea leer ist.
         */
        const toggle_approve_button = () => {
            approve_button.toggleClass( 'disabled', textarea_is_empty() )
        }

        if ( check.reason_required ) {
            textarea.val( check.reason )

            form.prop( 'hidden', false )

            textarea.off( 'input.stop_my_time' ).on( 'input.stop_my_time', toggle_approve_button )
            toggle_approve_button()
        } else {
            form.prop( 'hidden', true )

            textarea.off( 'input.stop_my_time' )
            approve_button.removeClass( 'disabled' )
        }

        modal.modal(
            {
                onApprove: async() => {
                    if ( ! form.prop( 'hidden' ) && textarea_is_empty() ) {
                        return false
                    }

                    if ( this.is_project_context() ) {
                        project.times_controller.disable_action_buttons()
                    }

                    await this.api_stop_my_time(
                        check,
                        check.reason_required ? String( textarea.val() ).trim() : null
                    )

                    if ( this.is_project_context() ) {
                        project.times_controller.enable_action_buttons()
                    }
                }
            }
        )
        modal.modal( 'show' )
    }

    /**
     * Beendet die eigene laufende Arbeitszeit.
     *
     * In der Montage werden alle Arbeitszeiten des Bereichs beendet, da sie
     * gemeinsam über ein Tablet gestartet wurden.
     *
     * @param check   Antwort der Vorabprüfung.
     * @param reason  Begründung bei Zeitüberschreitung, sonst null.
     */
    async api_stop_my_time( check: GT_Stop_Time_Check, reason: string | null = null ) {
        const TASK = check.is_assembly ? 'Beenden der Arbeitszeiten' : 'Beenden der Arbeitszeit'

        const route = check.is_assembly ? 'goehler-tool/v1/times/stop-times' : 'goehler-tool/v1/times/stop-time'

        const data: Record<string, unknown> = check.is_assembly ? { post_id: check.post_id, status: check.status } : {}

        if ( reason ) {
            data.reason = reason
        }

        console.log( `[REST] ${TASK}`, data )

        this.disable_stop_button()

        try {
            const response = await this.rest_client.post( route, data ) as { toast: string }

            console.log( `${TASK}?`, response )

            app.show_toast_ajax_response( response.toast )

            if ( this.is_project_context() ) {
                await project.times_controller.api_load_times()
            } else {
                await this.api_load_my_running_times()
            }
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_stop_button()
        }
    }

    /**
     * Aktualisiert die Leiste anhand der eigenen laufenden Arbeitszeit.
     */
    check_my_running_times() {
        if ( ! this.running_time ) {
            this.disable_running_times_mode()
            return
        }

        const container = document.querySelector( '.time-indicator' )

        if ( ! container ) {
            return
        }

        const project_button = container.querySelector<HTMLAnchorElement>( '[data-action="open-running-project"]' )

        if ( ! project_button ) {
            return
        }

        project_button.href = `${this.running_time.project.permalink}?action-tab=${this.running_time.status.slug}`

        const label = project_button.querySelector( 'span' )

        if ( label ) {
            label.textContent = this.running_time.project.title
        }

        this.enable_running_times_mode()
    }

    enable_running_times_mode() {
        document.querySelector( '.time-indicator.demo' )?.classList.remove( 'hidden' )
    }

    disable_running_times_mode() {
        document.querySelector( '.time-indicator.demo' )?.classList.add( 'hidden' )
    }

    disable_stop_button() {
        jQuery( '[data-action="stop-my-time"]' ).addClass( 'disabled loading' )
    }

    enable_stop_button() {
        jQuery( '[data-action="stop-my-time"]' ).removeClass( 'disabled loading' )
    }
}

export class Project_Times_Controller extends Times_Controller {
    workers: GT_User[] = []
    poll_id: number|undefined
    time_delta: number|undefined
    reason_sync_id?: number

    /** Zählt Schreibzugriffe auf die Arbeitszeiten, um veraltete Poll-Antworten zu verwerfen. */
    times_version = 0

    constructor() {
        super()
    }

    ready() {
        console.debug( 'ready: Times_Controller' )
        this.init_actions()
        this.init_events()
        this.init_dropdowns()
        this.init_calendars()
        this.init_forms()
        this.render_times()
        this.start_polling()
    }

    init_events() {
        jQuery( '.status-timetable[data-editable-times]' ).on(
            'click',
            '.list-item[data-type="time-entry"]:not(.running)',
            ( e: ClickEvent ) => this.init_update_time_modal( Number( jQuery( e.currentTarget ).data( 'postId' ) ) )
        )

        jQuery( '.status-times-container[data-editable-reason], .status-timetable[data-editable-reason]' ).on(
            'click',
            '.status-reason',
            ( e: ClickEvent ) => this.init_update_project_status_reason_modal(
                String( jQuery( e.currentTarget ).closest( '[data-project-status]' ).data( 'projectStatus' ) )
            )
        )
    }

    init_actions() {
        jQuery( '[data-action="start-time"]' ).on( 'click', () => this.start_time_confirmation() )
        jQuery( '[data-action="stop-time"]' ).on( 'click', () => this.stop_time_confirmation() )
        jQuery( '[data-action="add-time"]' ).on( 'click', () => this.init_add_time_modal() )
        jQuery( '[data-action="delete-time"]' ).on( 'click', ( e: ClickEvent ) => this.delete_time_confirmation( e.currentTarget ) )
    }

    init_add_time_modal() {
        const modal = project.get_modal( 'add-time' )

        if ( ! modal ) {
            return
        }

        // jQuery( '#form-add-time' ).form( 'clear' )
        //
        // // Fomantic-Komponenten werden von form( 'clear' ) nicht erfasst
        // jQuery( '#add-time-worker' ).dropdown( 'clear' )
        // jQuery( '#add-time-date-calendar' ).calendar( 'clear' )
        // jQuery( '#add-time-duration' ).val( '' )

        modal.modal( 'show' )
    }

    init_calendars() {
        jQuery( '#add-time-date-calendar' )
            .calendar(
                {
                    type: 'date',
                    selectAdjacentDays: true,
                    firstDayOfWeek: 1,
                    maxDate: DateTime.now().startOf( 'day' ).toJSDate(),
                    touchReadonly: false,
                    showWeekNumbers: true,
                    monthFirst: false,
                    context: 'body',
                    today: true,
                    formatter: {
                        date: 'DD.MM.YYYY',
                    },
                    text: app.calendar_text,
                    popupOptions: {
                        position: 'top left',
                    },
                }
            )

        jQuery( '#update-time-date-calendar' )
            .calendar(
                {
                    type: 'date',
                    selectAdjacentDays: true,
                    firstDayOfWeek: 1,
                    maxDate: DateTime.now().startOf( 'day' ).toJSDate(),
                    touchReadonly: false,
                    showWeekNumbers: true,
                    monthFirst: false,
                    context: 'body',
                    today: true,
                    formatter: {
                        date: 'DD.MM.YYYY',
                    },
                    text: app.calendar_text,
                    popupOptions: {
                        position: 'top left',
                    },
                }
            )

        jQuery( '#add-time-start-time' ).on(
            'change',
            function() {
                if ( jQuery( '#add-time-end-time' ).val() === '' ) {
                    return
                }

                jQuery( '#add-time' ).form( 'validate field', 'add-time-end-time' )
            }
        )
    }

    init_forms() {

        // @ts-expect-error Overload.
        jQuery.fn.form.settings.rules.timeAfter = function( value: string, identifier: string ): boolean {
            console.log( 'VALIDATION', value, identifier )
            const reference = jQuery( this as never )
                .closest( 'form' )
                .find( `[name="${ identifier }"]` )
                .val() as string

            const start = Duration.fromISOTime( reference || '' )
            const end = Duration.fromISOTime( value || '' )

            if ( ! start.isValid || ! end.isValid ) {
                return true
            }

            return end.toMillis() > start.toMillis()
        }

        const add_time_form = jQuery( '#form-add-time' )

        add_time_form.form(
            {
                fields: {
                    'add-time-worker': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte einen Mitarbeiter auswählen..'
                            }
                        ]
                    },
                    'add-time-date': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte ein Datum definieren.'
                            }
                        ]
                    },
                    'add-time-start-time': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Start-Uhrzeit definieren.'
                            }
                        ]
                    },
                    'add-time-end-time': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Stop-Uhrzeit definieren.'
                            },
                            {
                                type: 'timeAfter[add-time-start-time]',
                                prompt: 'Die Stop-Uhrzeit muss nach der Start-Uhrzeit liegen.'
                            }
                        ]
                    }
                },
                errorFocus: false,
                revalidate: true,
                onSuccess: ( e: Event ) => {
                    e.preventDefault()

                    void this.api_add_time()
                },
                onFailure: Toast.show_invalid_form,
            }
        )

        add_time_form.on(
            'change',
            '#add-time-start-time, #add-time-end-time',
            () => {
                this.render_add_time_duration( add_time_form )

                if ( add_time_form.find( '#add-time-end-time' ).val() !== '' ) {
                    add_time_form.form( 'validate field', 'add-time-end-time' )
                }
            }
        )

        const update_time_form = jQuery( '#form-update-time' )

        update_time_form.form(
            {
                fields: {
                    'update-time-date': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte ein Datum definieren.'
                            }
                        ]
                    },
                    'update-time-start-time': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Start-Uhrzeit definieren.'
                            }
                        ]
                    },
                    'update-time-end-time': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Stop-Uhrzeit definieren.'
                            },
                            {
                                type: 'timeAfter[update-time-start-time]',
                                prompt: 'Die Stop-Uhrzeit muss nach der Start-Uhrzeit liegen.'
                            }
                        ]
                    }
                },
                errorFocus: false,
                revalidate: true,
                onSuccess: ( e: Event ) => {
                    e.preventDefault()

                    void this.api_update_time()
                },
                onFailure: Toast.show_invalid_form,
            }
        )

        update_time_form.on(
            'change',
            '#update-time-start-time, #update-time-end-time',
            () => {
                this.render_update_time_duration( update_time_form )

                if ( update_time_form.find( '#update-time-end-time' ).val() !== '' ) {
                    update_time_form.form( 'validate field', 'update-time-end-time' )
                }
            }
        )

        jQuery( '#form-project-status-reason-update' ).form(
            {
                fields: {
                    'project-status-reason-update': {
                        rules: [
                            {
                                type: 'notEmpty',
                                prompt: 'Bitte eine Begründung eingeben.'
                            }
                        ]
                    }
                },
                errorFocus: false,
                revalidate: true,
                onSuccess: ( e: Event ) => {
                    e.preventDefault()

                    void this.api_update_project_status_reason()
                },
                onFailure: Toast.show_invalid_form,
            }
        )
    }

    init_dropdowns() {
        jQuery( '#start-time-workers' ).dropdown(
            {
                clearable: true,

                onChange: ( value, text, $choice ) => {
                    console.debug( 'start time workers onchange', value, text, $choice )

                    if ( value?.length ) {
                        this.enable_confirmation_button( 'start-time-confirmation' )
                    } else {
                        this.disable_confirmation_button( 'start-time-confirmation' )
                    }
                }
            }
        )

        jQuery( '#add-time-worker' ).dropdown()
    }

    /**
     * Speichert die Begründung für den im Formular hinterlegten Bereich.
     */
    async api_update_project_status_reason() {
        console.log( '[REST] update project status reason' )

        const TASK = 'Speichern der Begründung'

        const form = jQuery( '#form-project-status-reason-update' )

        const data: Record<string, unknown> = {
            post_id: gt_ajax_object.post_id,
            status: String( form.find( '#project-status-reason-status' ).val() ),
            reason: String( form.find( '#project-status-reason-update' ).val() ).trim(),
        }

        const button = form.find( '.submit.button' )

        button.addClass( 'loading disabled' ).prop( 'disabled', true )

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/projects/update-status-reason',
                data
            ) as {
                toast: string,
                reasons: object
            }

            console.log( `${ TASK }?`, response )

            app.show_toast_ajax_response( response.toast )

            project.set_meta( 'project-status-reasons', response.reasons )
            project.render_meta( 'project-status-reasons' )

            app.close_modals()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            button.removeClass( 'loading disabled' ).prop( 'disabled', false )
        }
    }

    /**
     * Öffnet das Modal zum Bearbeiten einer Arbeitszeit und befüllt die Felder.
     *
     * @param post_id Post-ID der Arbeitszeit.
     */
    init_update_time_modal( post_id: number ) {
        const modal = app.get_modal( 'update-time' )

        if ( ! modal ) {
            return
        }

        const time = project.times.find( ( el: GT_Time ): boolean => Number( el.post_id ) === post_id )

        if ( ! time || ! time.end_date ) {
            return
        }

        const form = modal.find( '#form-update-time' )

        const start = DateTime.fromSQL( time.start_date )
        const end = DateTime.fromSQL( time.end_date )

        form.find( '#time-post-id' ).val( post_id )
        form.find( '#update-time-worker' ).val( time.start_user.name )
        form.find( '#update-time-start-time' ).val( start.toFormat( 'HH:mm' ) )
        form.find( '#update-time-end-time' ).val( end.toFormat( 'HH:mm' ) )

        modal.find( '#update-time-date-calendar' ).calendar( 'set date', start.toJSDate(), true, false )

        this.render_update_time_duration( form )

        modal.modal( 'show' )
    }

    /**
     * Öffnet das Modal zum Bearbeiten der Gründe eines Bereichs.
     *
     * @param status Slug des Bereichs.
     */
    init_update_project_status_reason_modal( status: string ) {
        const modal = app.get_modal( 'update-project-status-reason' )

        if ( ! modal ) {
            return
        }

        const reasons = project.get_meta_value( 'project-status-reasons' )

        modal.find( '#project-status-reason-status' ).val( status )
        modal.find( '#project-status-reason-update' ).val( reasons?.[ status ] ?? '' )

        const badge = modal.find( '.project-status-reason-context span.badge' )

        badge.attr( 'data-project-status', status )
        badge.text( project.get_meta_value( 'project-title' ) )

        modal.modal( 'show' )
    }

    /**
     * Aktualisiert die Dauer-Anzeige aus Start- und Stop-Uhrzeit.
     * Leert das Feld, solange die Werte unvollständig oder ungültig sind.
     */
    render_add_time_duration( form: JQuery ) {
        const start = Duration.fromISOTime( ( form.find( '#add-time-start-time' ).val() as string ) || '' )
        const end = Duration.fromISOTime( ( form.find( '#add-time-end-time' ).val() as string ) || '' )

        const valid = start.isValid && end.isValid && end.toMillis() > start.toMillis()

        form.find( '#add-time-duration' ).val(
            valid ? end.minus( start ).toFormat( 'h:mm' ) : ''
        )
    }

    /**
     * Aktualisiert die Dauer-Anzeige aus Start- und Stop-Uhrzeit.
     * Leert das Feld, solange die Werte unvollständig oder ungültig sind.
     */
    render_update_time_duration( form: JQuery ) {
        const start = Duration.fromISOTime( ( form.find( '#update-time-start-time' ).val() as string ) || '' )
        const end = Duration.fromISOTime( ( form.find( '#update-time-end-time' ).val() as string ) || '' )

        const valid = start.isValid && end.isValid && end.toMillis() > start.toMillis()

        form.find( '#update-time-duration' ).val(
            valid ? end.minus( start ).toFormat( 'h:mm' ) : ''
        )
    }

    /**
     * Legt eine Arbeitszeit über die REST-API an.
     *
     */
    async api_add_time() {
        console.log( '[REST] add time' )

        const TASK = 'Anlegen der Arbeitszeit'

        const form = jQuery( '#form-add-time' )

        // Der Calendar-Input enthält das deutsche Anzeigeformat,
        // deshalb das Datum über die API auslesen
        const date = form.find( '#add-time-date-calendar' ).calendar( 'get date' ) as Date | null

        if ( ! date ) {
            return
        }

        const data: Record<string, unknown> = {
            post_id: gt_ajax_object.post_id,
            status: action_tab,
            user_id: Number( form.find( '#add-time-worker' ).val() ),
            start_date: DateTime.fromJSDate( date ).toFormat( 'yyyy-MM-dd' ),
            start_time: form.find( '#add-time-start-time' ).val() as string,
            end_time: form.find( '#add-time-end-time' ).val() as string,
        }

        this.disable_action_buttons()

        const button = form.find( '.submit.button' )

        button.addClass( 'loading disabled' ).prop( 'disabled', true )

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/add-time',
                data
            ) as {
                toast: string,
                properties: [],
                time: GT_Time,
                times: GT_Time[],
                reason_required: boolean,
                status: string
            }

            console.log( `${ TASK }?`, response )

            app.show_toast_ajax_response( response.toast )

            project.update_properties_meta( response.properties )
            project.times = response.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()

            const add_time_modal = project.get_modal( 'add-time' )

            if ( response.reason_required && add_time_modal ) {
                this.chain_project_status_reason_modal( add_time_modal, response.status )
            }

            app.close_modals()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            button.removeClass( 'loading disabled' ).prop( 'disabled', false )
            this.enable_action_buttons()
        }
    }

    /**
     * Speichert die geänderte Arbeitszeit über die REST-API.
     */
    async api_update_time() {
        console.log( '[REST] update time' )

        const TASK = 'Speichern der Arbeitszeit'

        const form = jQuery( '#form-update-time' )

        // Der Calendar-Input enthält das deutsche Anzeigeformat,
        // deshalb das Datum über die API auslesen
        const date = form.find( '#update-time-date-calendar' ).calendar( 'get date' ) as Date | null

        if ( ! date ) {
            return
        }

        const data: Record<string, unknown> = {
            post_id: Number( form.find( '#time-post-id' ).val() ),
            project_post_id: gt_ajax_object.post_id,
            start_date: DateTime.fromJSDate( date ).toFormat( 'yyyy-MM-dd' ),
            start_time: form.find( '#update-time-start-time' ).val() as string,
            end_time: form.find( '#update-time-end-time' ).val() as string,
        }

        this.disable_action_buttons()

        const submit_button = form.find( '.submit.button' )
        const delete_button = form.find( '[data-action="delete-time"]' )

        submit_button.addClass( 'loading disabled' ).prop( 'disabled', true )
        delete_button.addClass( 'disabled' ).prop( 'disabled', true )

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/update-time',
                data
            ) as {
                toast: string,
                properties: [],
                time: GT_Time,
                times: GT_Time[],
                reason_required: boolean,
                status: string
            }

            console.log( `${ TASK }?`, response )

            app.show_toast_ajax_response( response.toast )

            project.update_properties_meta( response.properties )
            project.times = response.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()

            const update_time_modal = project.get_modal( 'update-time' )

            if ( response.reason_required && update_time_modal ) {
                this.chain_project_status_reason_modal( update_time_modal, response.status )
            }

            app.close_modals()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            submit_button.removeClass( 'loading disabled' ).prop( 'disabled', false )
            delete_button.removeClass( 'disabled' ).prop( 'disabled', false )
            this.enable_action_buttons()
        }
    }

    /**
     * Öffnet das Modal für die Begründung, sobald das übergebene Modal
     * geschlossen ist. Verhindert, dass Fomantic den Dimmer mit abbaut.
     *
     * @param modal  Das aktuell offene Modal.
     * @param status Slug des Bereichs.
     */
    chain_project_status_reason_modal( modal: JQuery, status: string ) {
        modal.modal( 'setting', 'onHidden', () => {
            modal.modal( 'setting', 'onHidden', function () {} )

            this.init_update_project_status_reason_modal( status )
        } )
    }

    /**
     * Fragt vor dem Löschen einer Arbeitszeit nach einer Bestätigung.
     *
     * @param target Der auslösende Button im Modal.
     */
    delete_time_confirmation( target: HTMLElement ) {
        const form = target.closest( 'form' ) as HTMLElement

        const post_id = jQuery( form ).find( '[name="time-post-id"]' ).val() as string

        if ( ! post_id ) {
            return
        }

        const modal = app.get_modal( 'delete-time-confirmation' )

        if ( ! modal ) {
            return
        }

        modal.modal(
            {
                onApprove: () => {
                    void this.api_delete_time( post_id )
                }
            }
        )
        modal.modal( 'show' )
    }

    /**
     * Löscht eine Arbeitszeit über die REST-API.
     *
     * @param post_id Post-ID der Arbeitszeit.
     */
    async api_delete_time( post_id: string ) {
        console.log( '[REST] delete time' )

        const TASK = 'Löschen der Arbeitszeit'

        const data: Record<string, unknown> = {
            post_id: Number( post_id ),
            project_post_id: gt_ajax_object.post_id,
        }

        this.disable_action_buttons()

        const delete_button = jQuery( '#form-update-time [data-action="delete-time"]' )
        const submit_button = jQuery( '#form-update-time .submit.button' )

        delete_button.addClass( 'loading disabled' ).prop( 'disabled', true )
        submit_button.addClass( 'disabled' ).prop( 'disabled', true )

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/delete-time',
                data
            ) as {
                toast: string,
                properties: [],
                times: GT_Time[]
            }

            console.log( `${ TASK }?`, response )

            app.show_toast_ajax_response( response.toast )

            project.update_properties_meta( response.properties )
            project.times = response.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()

            app.close_modals()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            delete_button.removeClass( 'loading disabled' ).prop( 'disabled', false )
            submit_button.removeClass( 'disabled' ).prop( 'disabled', false )
            this.enable_action_buttons()
        }
    }

    disable_action_buttons() {
        jQuery( '[data-action="start-time"]' ).addClass( 'disabled loading' )
        jQuery( '[data-action="stop-time"]' ).addClass( 'disabled loading' )
        jQuery( '[data-action="add-time"]' ).addClass( 'disabled loading' )
    }

    enable_action_buttons() {
        jQuery( '[data-action="start-time"]' ).removeClass( 'disabled loading' )
        jQuery( '[data-action="stop-time"]' ).removeClass( 'disabled loading' )
        jQuery( '[data-action="add-time"]' ).removeClass( 'disabled loading' )
    }

    disable_confirmation_button( modal_id: string ) {
        const modal = app.get_modal( modal_id )

        if ( ! modal ) {
            return
        }

        const button = modal.find( '.ui.positive.button' )

        button.addClass( 'disabled' )
    }

    enable_confirmation_button( modal_id: string ) {
        const modal = app.get_modal( modal_id )

        if ( ! modal ) {
            return
        }

        const button = modal.find( '.ui.positive.button' )

        button.removeClass( 'disabled' )
    }

    /**
     * Liefert die im Dropdown ausgewählten Mitarbeiter-IDs.
     */
    get_start_time_user_ids(): number[] {
        const values = jQuery( '#start-time-workers' ).dropdown( 'get values' ) as string[]

        return values.map( Number )
    }

    start_time_confirmation() {
        console.log( 'start time?', project.post_id )

        const modal = app.get_modal( 'start-time-confirmation' )

        if ( ! modal ) {
            return
        }

        this.reset_time_workers()

        modal.modal(
            {
                onApprove: () => {
                    console.log( 'approved' )

                    switch ( action_tab ) {
                        case 'assembly':
                            void this.api_start_times()
                            break
                        default:
                            void this.api_start_time()
                            break
                    }
                }
            }
        )
        modal.modal( 'show' )
    }

    reset_time_workers() {
        console.log( 'reset workers' )
        const dropdown = jQuery( '#start-time-workers' )

        if ( ! dropdown ) {
            return
        }

        dropdown.dropdown( 'clear' )

        dropdown.dropdown( 'set selected', gt_current_user.user_id )
    }

    stop_time_confirmation() {
        console.log( 'stop time?', project.post_id )

        const modal = app.get_modal( 'stop-time-confirmation' )

        if ( ! modal ) {
            return
        }

        const form = jQuery( '#form-project-status-reason-edit' )
        const textarea = jQuery( '#project-status-reason-edit' )
        const approve_button = modal.find( '.positive.button' )

        /**
         * Prüft, ob die Begründungs-Textarea leer ist.
         */
        const textarea_is_empty = () => String( textarea.val() ?? '' ).trim() === ''

        /**
         * Blockiert den Approve-Button, solange die Textarea leer ist.
         */
        const toggle_approve_button = () => {
            approve_button.toggleClass( 'disabled', textarea_is_empty() )
        }

        const project_badge = modal.find( '.stop-time-context span.badge' )

        project_badge.attr( 'data-project-status', action_tab )
        project_badge.text( project.get_meta_value( 'project-title' ) )

        /**
         * Synchronisiert Sichtbarkeit und Pflichtstatus der Begründung.
         *
         * Die Textarea wird nur beim Wechsel von versteckt auf sichtbar befüllt,
         * damit laufende Eingaben nicht überschrieben werden.
         */
        const sync_reason_form = () => {
            const required = Boolean( this.time_delta && this.time_delta > gt_time_config.reason_threshold )

            if ( ! required ) {
                form.prop( 'hidden', true )
                textarea.off( 'input.stop_time' )
                approve_button.removeClass( 'disabled' )
                return
            }

            if ( ! form.prop( 'hidden' ) ) {
                return
            }

            const reasons = project.get_meta_value( 'project-status-reasons' )

            textarea.val( reasons?.[ action_tab ] ?? '' )
            form.prop( 'hidden', false )
            textarea.off( 'input.stop_time' ).on( 'input.stop_time', toggle_approve_button )
            toggle_approve_button()
        }

        modal.modal(
            {
                onShow: () => {
                    sync_reason_form()

                    this.reason_sync_id = window.setInterval( sync_reason_form, 10 * 1000 )
                },
                onHidden: () => {
                    window.clearInterval( this.reason_sync_id )

                    this.reason_sync_id = undefined
                },
                onApprove: () => {
                    if ( ! form.prop( 'hidden' ) && textarea_is_empty() ) {
                        return false
                    }

                    const reason = form.prop( 'hidden' ) ? null : String( textarea.val() ).trim()

                    switch ( action_tab ) {
                        case 'assembly':
                            void this.stop_times( reason )
                            break
                        default:
                            void this.stop_time( reason )
                            break
                    }
                }
            }
        )
        modal.modal( 'show' )
    }

    /**
     * Beendet alle laufenden Arbeitszeiten des Bereichs.
     *
     * @param reason  Begründung bei Zeitüberschreitung, sonst null.
     */
    async stop_times( reason: string | null = null ) {
        const TASK = 'Beenden der Arbeitszeiten'

        const data: Record<string, unknown> = {
            post_id: gt_ajax_object.post_id,
            status: action_tab,
        }

        if ( reason ) {
            data.reason = reason
        }

        console.log( `[REST] ${TASK}`, data )

        this.disable_action_buttons()

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/stop-times',
                data
            ) as {
                toast: string,
                properties: [],
                reasons: [],
                times: GT_Time[]
            }
            console.log( `${TASK}?`, response )

            app.show_toast_ajax_response( response.toast )

            project.update_properties_meta( response.properties )
            project.set_meta( 'project-status-reasons', response.reasons )
            project.times = response.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_action_buttons()
            await app.times_controller.api_load_my_running_times()
        }
    }

    /**
     * Beendet die eigene laufende Arbeitszeit.
     *
     * @param reason  Begründung bei Zeitüberschreitung, sonst null.
     */
    async stop_time( reason: string | null = null ) {
        const TASK = 'Beenden der Arbeitszeit'

        const data: Record<string, unknown> = {}

        if ( reason ) {
            data.reason = reason
        }

        console.log( `[REST] ${TASK}`, data )

        this.disable_action_buttons()

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/stop-time',
                data
            ) as {
                toast: string,
                projects: Record<string, {
                    times: GT_Time[],
                    properties: object,
                    reasons: object
                }>
            }

            console.log( `${TASK}?`, response )

            app.show_toast_ajax_response( response.toast )

            await app.times_controller.api_load_my_running_times()

            const current_project = response.projects?.[ project.post_id ]

            if ( ! current_project ) {
                return
            }

            project.update_properties_meta( current_project.properties )
            project.set_meta( 'project-status-reasons', current_project.reasons )
            project.times = current_project.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_action_buttons()
        }
    }

    async api_start_time() {
        const TASK = 'Starten der Arbeitszeit'

        const data = {
            post_id: gt_ajax_object.post_id,
            status: action_tab,
        }

        console.log( '[REST]', data )

        this.disable_action_buttons()

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/start-time',
                data
            ) as {
                toast: string,
                properties: [],
                time: GT_Time,
                times: GT_Time[]
            }
            console.log( `${TASK}?`, response )

            app.show_toast_ajax_response( response.toast )

            project.update_properties_meta( response.properties )
            project.times = response.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_action_buttons()
            await app.times_controller.api_load_my_running_times()
        }
    }

    async api_start_times() {
        const TASK = 'Starten der Arbeitszeiten'

        const data = {
            post_id: gt_ajax_object.post_id,
            user_ids: this.get_start_time_user_ids(),
            status: action_tab,
        }

        console.log( '[REST]', data )

        this.disable_action_buttons()

        try {
            const response = await this.rest_client.post(
                'goehler-tool/v1/times/start-times',
                data
            ) as {
                toast: string,
                properties: [],
                times: GT_Time[]
            }
            console.log( `${TASK}?`, response )

            app.show_toast_ajax_response( response.toast )

            project.update_properties_meta( response.properties )
            project.times = response.times
            this.times_version++
            this.render_status_timetable()
            this.render_times()
        } catch ( error ) {
            handle_api_error( error, TASK )
        } finally {
            this.enable_action_buttons()
            await app.times_controller.api_load_my_running_times()
        }
    }

    /**
     * Rendert die Arbeitszeiten und aktualisiert die Markierungen.
     */
    render_times() {
        const running = project.times.filter( ( el: GT_Time ) => 'running' in el.properties )

        gt_time_status.forEach( ( status ) => {
            const is_running = running.some( ( el: GT_Time ) => el.status.slug === status )

            const tab = document.querySelector( `.project-progress__item[data-project-status="${status}"]` )
            const approval = document.querySelector( `.temp-approvals .approval[data-status="${status}"]` )

            tab?.classList.toggle( 'active', is_running )
            approval?.classList.toggle( 'active', is_running )
        } )

        if ( document.querySelector( '.status-times-container' ) ) {
            this.render_status_times()
        }
    }

    /**
     * Lädt die Arbeitszeiten des Projekts und rendert sie.
     */
    async api_load_times() {
        const TASK = 'Laden der Arbeitszeiten'

        const version = this.times_version

        try {
            const response = await this.rest_client.get(
                'goehler-tool/v1/times/load-project-times',
                {
                    post_id: Number( gt_ajax_object.post_id )
                }
            ) as GT_Time[]

            // Verwirft die Antwort, wenn zwischenzeitlich ein
            // Schreibzugriff die Arbeitszeiten aktualisiert hat
            if ( version !== this.times_version ) {
                console.log( 'rerender times blocked', version, this.times_version )
                return
            }

            project.set_times( response )

            this.render_status_timetable()
            this.render_times()
        } catch ( error ) {
            if ( error instanceof Api_Error && error.status === 0 ) {
                console.warn( 'Arbeitszeiten konnten nicht geladen werden', error )
                return
            }

            if ( error instanceof Api_Error && error.is_auth_error() ) {
                window.clearInterval( this.poll_id )
            }

            handle_api_error( error, TASK )
        }

        await app.times_controller.api_load_my_running_times()
    }

    start_polling() {
        this.poll_id = window.setInterval( () => {
            if ( document.visibilityState === 'hidden' ) {
                return
            }

            void this.api_load_times()
        }, 30 * 1000 )
    }

    /**
     * ZEITÜBERSICHT
     */

    render_status_times() {
        const container: HTMLElement|null = document.querySelector( '.status-times-container' )

        if ( ! container ) {
            return
        }

        const start_dates = project.get_meta_value( 'project-start-dates' )

        if ( ! start_dates ) {
            return
        }

        let total_delta = 0

        for ( const status of [ 'cutting', 'cnc', 'finishing', 'fitting', 'assembly' ] ) {
            if ( ! ( status in project.get_meta_value( 'project-start-dates' ) ) ) {
                continue
            }

            const delta = this.render_status_times_data( container, status )
            total_delta += delta ? delta : 0
        }

        const value = container.querySelector( '.value' ) as HTMLElement
        value.innerHTML = `${minutes_to_duration( total_delta )} Stunden`

        if ( total_delta > 0 ) {
            value.classList.remove( 'good' )
        } else {
            value.classList.add( 'good' )
        }

        project.render_meta( 'project-status-reasons' )
    }

    render_status_times_data( container: HTMLElement, status: string ) {
        console.log( 'render status times', status )

        const status_container = container.querySelector( `.meta-segment[data-project-status="${status}"]` )

        if ( ! status_container ) {
            return
        }

        const status_times = project.times.filter( ( el: GT_Time ) => el.status.slug === status )

        const running = status_times.some( ( el: GT_Time ) => 'running' in el.properties )

        if ( running ) {
            status_container.classList.add( 'active' )
        } else {
            status_container.classList.remove( 'active' )
        }

        console.log( project.times, status, status_times )

        const list = status_container.querySelector( '.list' ) as HTMLElement
        list.innerHTML = ''

        const totals = {
            'times': 0,
            'extra': 0,
            'support': 0,
            'net': 0,
            'estimate': 0,
        }

        const estimate_duration = project.get_meta_value( 'project-planning-estimates' )[status]

        if ( estimate_duration ) {
            totals.estimate = duration_in_minutes( estimate_duration )
        }

        totals.times = status_times.reduce(
            ( accumulator: number, currentValue: GT_Time ): number => {
                let minutes = 0

                if ( currentValue.duration ) {
                    minutes = currentValue.duration
                } else {
                    const start_sql  = currentValue.start_date
                    const start_time = DateTime.fromSQL( start_sql )
                    const diff_min   = start_time.diffNow( 'minutes' ).minutes
                    minutes = Math.abs( diff_min )
                    minutes = Math.ceil( minutes )
                }

                return accumulator + minutes
            },
            0
        )

        // (2) extra work.
        totals.extra = project.extra_work.filter( el => el.status.slug === status ).reduce(
            ( accumulator: number, currentValue: aby ) => {
                console.log( accumulator, currentValue )
                return accumulator + duration_in_minutes( currentValue.duration )
            },
            0
        )

        // (3) Support
        totals.support = project.support.filter( el => el.status.slug === status ).reduce(
            ( accumulator, currentValue ) => {
                console.log( accumulator, currentValue )
                return accumulator + duration_in_minutes( currentValue.duration )
            },
            0
        )

        // (4) net time
        const li_net = document.createElement( 'li' )

        totals.net = totals.times - totals.extra + totals.support

        console.log( 'net time', totals.net, 'estimate time', totals.estimate )

        li_net.classList.add( 'list-item' )
        li_net.dataset.type = 'highlighted'

        li_net.innerHTML = `
			<div class="item-title">Effektive Arbeitszeit</div>
			<div class="item-description">${minutes_to_duration( totals.net )} h</div>
		`
        list.append( li_net )

        // (5) estimate time
        const li_estimate = document.createElement( 'li' )

        li_estimate.classList.add( 'list-item' )
        li_estimate.dataset.type = 'estimate'

        li_estimate.innerHTML = `
			<div class="item-icon"><i class="minus icon"></i></div>	
			<div class="item-title">Soll-Schätzzeit</div>
			<div class="item-description">${minutes_to_duration( totals.estimate )} h</div>
		`
        list.append( li_estimate )

        // (6) delta
        const li_delta = document.createElement( 'li' )

        const time_status = ( totals.estimate >= totals.net ? 'good' : 'bad' )

        li_delta.classList.add( 'list-item' )
        li_delta.dataset.type = 'delta'
        li_delta.dataset.status = time_status

        li_delta.innerHTML = `
			<div class="item-icon"><i class="angle double right icon"></i></div>
			<div class="item-title">Saldo</div>
			<div class="item-description">${minutes_to_duration( totals.net - totals.estimate )} h</div>
		`
        list.append( li_delta )

        return totals.net - totals.estimate
    }

    /**
     * Rendert die Zeitübersicht in der Seitenliste mit Berechnungen der Arbeitszeiten.
     */
    render_status_timetable() {
        console.debug( 'render status timetable' )

        const container = document.querySelector( 'section.status-timetable' )

        if ( ! container ) {
            return
        }

        container.classList.remove( 'active' )

        const list = container.querySelector( '.list' ) as HTMLElement

        const status_times = project.times.filter( ( el: GT_Time ): boolean => el.status.slug === action_tab )

        const totals = {
            'times': 0,
            'extra': 0,
            'support': 0,
            'net': 0,
            'estimate': 0,
        }

        const estimate_duration = project.get_meta_value( 'project-planning-estimates' )[action_tab]

        if ( estimate_duration ) {
            totals.estimate = duration_in_minutes( estimate_duration )
        }

        list.innerHTML = ''

        // (1) total time.
        totals.times = status_times.reduce(
            ( accumulator, currentValue ) => {
                // console.log( accumulator, currentValue )

                let minutes = 0

                if ( currentValue.duration ) {
                    minutes = currentValue.duration
                } else {
                    const start_sql  = currentValue.start_date
                    const start_time = DateTime.fromSQL( start_sql )
                    const diff_min   = start_time.diffNow( 'minutes' ).minutes
                    minutes = Math.abs( diff_min )
                    minutes = Math.ceil( minutes )
                }

                return accumulator + minutes
            },
            0
        )

        const li_total = document.createElement( 'li' )

        li_total.classList.add( 'list-item' )

        li_total.innerHTML = `
			<div class="item-title">Arbeitszeiten</div>
			<div class="item-description">${minutes_to_duration( totals.times )} h</div>
		`
        list.append( li_total )

        for ( const el of status_times ) {
            const li = document.createElement( 'li' )
            li.classList.add( 'list-item', 'detail' )
            li.dataset.type = 'time-entry'
            li.dataset.postId = el.post_id

            let duration = 0

            if ( el.duration ) {
                duration = el.duration
            } else {
                const start_sql  = el.start_date
                const start_time = DateTime.fromSQL( start_sql )
                const minutes      = start_time.diffNow( 'minutes' ).minutes
                duration = Math.abs( minutes )
                duration = Math.ceil( duration )
                li.classList.add( 'running' )
            }

            if ( el.start_user.id === Number( gt_current_user.user_id ) ) {
                li.dataset.currentUser = 'true'
            }

            li.innerHTML = `
				<div class="item-icon"></div>
				<div class="item-title">
					<div class="item-start-date">${format_date( el.start_date )} - ${format_time( el.start_date )}</div>
					<div class="item-start-user"><i class="user icon"></i> ${el.start_user.name}</div>
					<div class="item-properties"></div>
				</div>
				<div class="item-description">${minutes_to_duration( duration )} h</div>
			`

            const properties = []

            if ( 'return' in el.properties ) {
                properties.push( 'Reklamation' )
            }

            if ( 'manual' in el.properties ) {
                properties.push( 'nachgetragen' )
            }

            if ( 'edited' in el.properties ) {
                properties.push( 'bearbeitet' )
            }

            if ( properties.length ) {
                const container = li.querySelector( '.item-properties' ) as HTMLElement
                container.innerHTML = properties.join( ', ' )
            }

            list.append( li )
        }

        // (2) extra work.
        totals.extra = project.extra_work.reduce(
            ( accumulator, currentValue ) => {
                // console.log( accumulator, currentValue )
                return accumulator + duration_in_minutes( currentValue.duration )
            },
            0
        )

        if ( project.extra_work.length ) {
            const li_work_total = document.createElement( 'li' )

            li_work_total.classList.add( 'list-item' )
            li_work_total.dataset.type = 'extra-work'

            li_work_total.innerHTML = `
				<div class="item-icon"><i class="minus icon"></i></div>	
				<div class="item-title">Mehrarbeiten</div>
				<div class="item-description">${minutes_to_duration( totals.extra )} h</div>
			`
            list.append( li_work_total )
        }

        for ( const el of project.extra_work.toReversed() ) {
            const li = document.createElement( 'li' )

            li.classList.add( 'list-item', 'detail' )
            li.dataset.type = 'extra-work-entry'
            li.dataset.postId = el.post_id

            li.innerHTML = `
				<div class="item-icon"></div>
				<div class="item-title">${el.title}</div>
				<div class="item-description">${el.duration} h</div>
			`

            if ( el.type.slug === 'support' ) {
                li.innerHTML = `
				<div class="item-icon"></div>
				<div class="item-title">${el.title}<br><small>für ${el.support.project.title}</small></div>
				<div class="item-description">${el.duration} h</div>
			`
            }


            list.append( li )
        }

        // (3) Support
        totals.support = project.support.reduce(
            ( accumulator, currentValue ) => {
                console.log( accumulator, currentValue )
                return accumulator + duration_in_minutes( currentValue.duration )
            },
            0
        )

        if ( project.support.length ) {
            const li_support_total = document.createElement( 'li' )

            li_support_total.classList.add( 'list-item' )
            li_support_total.dataset.type = 'support'

            li_support_total.innerHTML = `
			<div class="item-icon"><i class="plus icon"></i></div>	
			<div class="item-title">Unterstützung</div>
			<div class="item-description">${minutes_to_duration( totals.support )} h</div>
		`
            list.append( li_support_total )
        }

        for ( const el of project.support.toReversed() ) {
            const li = document.createElement( 'li' )

            li.classList.add( 'list-item', 'detail' )
            li.dataset.type = 'support-entry'
            li.dataset.postId = el.post_id

            li.innerHTML = `
				<div class="item-icon"></div>
				<div class="item-title">${el.title}<br><small>von ${el.author.name}</small></div>
				<div class="item-description">${el.duration} h</div>
			`
            list.append( li )
        }

        // (4) net time
        const li_net = document.createElement( 'li' )

        totals.net = totals.times - totals.extra + totals.support

        console.log( 'net time', totals.net, 'estimate time', totals.estimate )

        li_net.classList.add( 'list-item' )
        li_net.dataset.type = 'highlighted'

        li_net.innerHTML = `
			<div class="item-title">Effektive Arbeitszeit</div>
			<div class="item-description">${minutes_to_duration( totals.net )} h</div>
		`
        list.append( li_net )

        // (5) estimate time
        const li_estimate = document.createElement( 'li' )

        li_estimate.classList.add( 'list-item' )
        li_estimate.dataset.type = 'estimate'

        li_estimate.innerHTML = `
			<div class="item-icon"><i class="minus icon"></i></div>	
			<div class="item-title">Soll-Schätzzeit</div>
			<div class="item-description">${minutes_to_duration( totals.estimate )} h</div>
		`
        list.append( li_estimate )

        // (6) delta
        const li_delta = document.createElement( 'li' )

        const time_status = ( totals.estimate >= totals.net ? 'good' : 'bad' )

        this.time_delta = totals.net - totals.estimate

        li_delta.classList.add( 'list-item' )
        li_delta.dataset.type = 'delta'
        li_delta.dataset.status = time_status

        li_delta.innerHTML = `
			<div class="item-icon"><i class="angle double right icon"></i></div>
			<div class="item-title">Saldo</div>
			<div class="item-description">${minutes_to_duration( totals.net - totals.estimate )} h</div>
		`
        list.append( li_delta )

        this.render_timetable_action_buttons()

        this.add_status_timetable_event_listeners()

        project.render_meta( 'project-status-reasons' )
    }

    render_timetable_action_buttons() {
        const container = document.querySelector( 'section.status-timetable' )

        if ( ! container ) {
            return
        }

        const actions = container.querySelector( '.item-actions' ) as HTMLElement

        if ( ! actions ) {
            return
        }

        // wenn produktion noch nicht begonnen wurde, dann beide Buttons ausblenden
        const properties = project.get_meta_value( 'project-properties' )

        if (
            ! ( 'production-started' in properties ) ||
            ( action_tab === project.get_meta_value( 'project-status' )?.slug &&
                ( project.get_meta_value( 'project-substatus' )?.slug.includes( 'waiting' ) || project.get_meta_value( 'project-substatus' )?.slug.includes( 'paused' ) ) )
        ) {
            console.log( 'hidden' )
            actions.dataset.hidden = 'true'
        } else {
            console.log( 'not hidden' )
            actions.dataset.hidden = 'false'

            if ( project.times.some( el => 'running' in el.properties && el.status.slug === action_tab && el.start_user.id === Number( gt_current_user.user_id ) ) ) {
                console.log( 'laufende Arbeitszeit vorhanden', gt_current_user.nickname )

                this.hide_start_buttons()
                this.show_stop_buttons()

                container.classList.add( 'active' )
            } else {
                this.show_start_buttons()
                this.hide_stop_buttons()

                container.classList.remove( 'active' )
            }
        }
    }

    show_stop_buttons() {
        const buttons  = document.querySelectorAll<HTMLElement>( 'button[data-action="stop-time"]' )

        for ( const button of Array.from( buttons ) ) {
            button.dataset.hidden = 'false'
        }
    }

    show_start_buttons() {
        const buttons  = document.querySelectorAll<HTMLElement>( 'button[data-action="start-time"]' )

        for ( const button of Array.from( buttons ) ) {
            button.dataset.hidden  = 'false'
        }
    }

    hide_stop_buttons() {
        const buttons  = document.querySelectorAll<HTMLElement>( 'button[data-action="stop-time"]' )

        for ( const button of Array.from( buttons ) ) {
            button.dataset.hidden = 'true'
        }
    }

    hide_start_buttons() {
        const buttons  = document.querySelectorAll<HTMLElement>( 'button[data-action="start-time"]' )

        for ( const button of Array.from( buttons ) ) {
            button.dataset.hidden  = 'true'
        }
    }

    /**
     * Erstellt Event-Listeners, die relevant für die Zeitübersicht sind.
     * Mehrarbeiten werden durch Mausinteraktion in anderen Bereichen visuell hervorgehoben.
     */
    add_status_timetable_event_listeners() {
        console.debug( 'add status timetable event listeners' )

        const container = document.querySelector( 'section.status-timetable' )

        if ( ! container ) {
            return
        }

        const items     = container.querySelectorAll( '.list-item[data-type="extra-work-entry"]' )

        for ( const item of items ) {
            item.addEventListener( 'mouseenter', () => project.add_highlight_extra_work( item.dataset.postId ) )
            item.addEventListener( 'mouseleave', () => project.remove_highlight_extra_work( item.dataset.postId ) )
        }

    }
}
